import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, CardTitle, Spinner } from "reactstrap";
import * as app_consts from "../../../../../../../../constants/react_app_consts";
import { Button, Col, Icon, Row } from "../../../../../../../../components/Component";
import TextInput from "../../../../../../../components/inputs/TextInput";
import ModalPreview from "../../../../../modal/modal_preview/ModalPreview";
import { COMPLETE_PROCESSING_DELIVERY_NOTE_API } from "../../../../../../../../api/api_data";
import {
  fail_complete_processing_delivery_note,
  loading_complete_processing_delivery_note,
  reset_complete_processing_delivery_note,
  success_complete_processing_delivery_note,
} from "../../../../../../../../reducers/dashboard_reducers/delivery_notes/complete_processing_delivery_note_Slice";
import {
  COMPLETE_PROCESSING_DELIVERY_NOTE_ACCESS_DENIED_MSG,
  COMPLETE_PROCESSING_DELIVERY_NOTE_ERR_MSG,
  COMPLETE_PROCESSING_DELIVERY_NOTE_SUCCESS_MSG,
} from "../../../../../../../../constants/success_error_messages_consts";
import { useSelector } from "react-redux";
import { employee_dashboard_actions_helper } from "../../../../../../../../actions/actions_helper";
import { useDispatch } from "react-redux";
import { messageToast } from "../../../../../../../../utils/Utils";
import { useParams } from "react-router";
import { resolveWeighbridgeTicketPrefillData } from "../../../../../../../../utils/weighbridgeTicketNormalization";
import {
  formatWeighbridgeWeightInputMt,
  isWeighbridgeWeightSequenceValid,
  resolveWeighbridgeNetWeight,
} from "../../../../../../../../utils/weighbridgeUtils";

const initialState = {
  delivery_note_id: "",
  first_weight: "",
  second_weight: "",
  net_weight: "",
  customer_receiving_name: "",
  customer_receiving_primary_mobile: "",
  customer_receiving_secondary_mobile: "",
  weigh_bridge_ticket_number: "",
  weigh_bridge_ticket_reference_no: "",
  truck_licence_plate: "",
  transporter_name: "",
  truck_driver_name: "",
  trailer_one: "",
  trailer_two: "",
  comments: "",
};

const convertTicketPrefillWeightsToMt = (ticketPrefill = {}) => ({
  ...ticketPrefill,
  first_weight: formatWeighbridgeWeightInputMt(ticketPrefill?.first_weight),
  second_weight: formatWeighbridgeWeightInputMt(ticketPrefill?.second_weight),
  net_weight: formatWeighbridgeWeightInputMt(ticketPrefill?.net_weight),
});

const FinalDetailsStep = ({
  weighBridgeFormData,
  continue_processing_data,
  setContinueProcessingData,
  nextStep,
  selectedWeighbridgeTicket,
}) => {
  const dispatch = useDispatch();
  const { delivery_note_ref_number } = useParams();

  const [refData, setRefData] = useState(weighBridgeFormData);
  const [stepData, setStepData] = useState(initialState);

  const [openPreview, setOpenPreview] = useState(false);
  const [errorVal, setErrorVal] = useState("");
  const [loading, setLoading] = useState(false);

  // ------------------------------------------------------
  // Source of truth for weights:
  // - Prefer DB payload: continue_processing_data.weigh_bridge_ticket
  // - Fallback to local/legacy fields on continue_processing_data / weighBridgeFormData
  // ------------------------------------------------------
  const wbFromDb = useMemo(() => {
    return continue_processing_data?.weigh_bridge_ticket || null;
  }, [continue_processing_data]);

  const wbFromSelection = useMemo(() => {
    return (
      weighBridgeFormData?.weighbridge_ticket_data ||
      selectedWeighbridgeTicket ||
      null
    );
  }, [selectedWeighbridgeTicket, weighBridgeFormData]);

  const selectedTicketPrefill = useMemo(() => {
    return convertTicketPrefillWeightsToMt(
      resolveWeighbridgeTicketPrefillData(wbFromSelection),
    );
  }, [wbFromSelection]);

  const ticketData = wbFromDb || selectedTicketPrefill || wbFromSelection || {};

  const tareFromDb =
    wbFromDb?.first_weight ??
    selectedWeighbridgeTicket?.first_weight ??
    selectedTicketPrefill?.first_weight ??
    wbFromSelection?.first_weight;
  const grossFromDb =
    wbFromDb?.second_weight ??
    selectedWeighbridgeTicket?.second_weight ??
    selectedTicketPrefill?.second_weight ??
    wbFromSelection?.second_weight;
  const netFromDb = resolveWeighbridgeNetWeight(
    wbFromDb?.net_weight ??
      selectedWeighbridgeTicket?.net_weight ??
      selectedTicketPrefill?.net_weight ??
      wbFromSelection?.net_weight,
    tareFromDb,
    grossFromDb,
  );

  // Lock weights if backend already knows them
  const weightsLocked = useMemo(() => {
    // If gross is present, we consider weighbridge weights final/known from DB
    return !!grossFromDb || !!netFromDb;
  }, [grossFromDb, netFromDb]);

  // Optional: if you also want to lock when ticket-driven selection is active
  const ticketLocked = !!weighBridgeFormData?.weighbridge_locked;

  useEffect(() => {
    // ------------------------------------------------------
    // Build "refData" and "stepData" consistently for:
    // 1) Continue processing (ref number exists)
    // 2) Create mode (no ref number)
    // ------------------------------------------------------
    if (delivery_note_ref_number) {
      // Continue processing: use DB payload shape
      const uniqueId = continue_processing_data?.user_account?.unique_identification_number;
      const dnId = continue_processing_data?.delivery_note?.delivery_note_id;

      setRefData({
        unique_identification_number: uniqueId,
        first_weight: tareFromDb ?? continue_processing_data?.first_weight ?? weighBridgeFormData?.first_weight ?? "",
        delivery_note_id: dnId,
      });

      setStepData((prev) => ({
        ...prev,
        delivery_note_id: dnId || prev.delivery_note_id,
        first_weight: tareFromDb ?? prev.first_weight,
        second_weight: grossFromDb ?? prev.second_weight,
        net_weight: netFromDb ?? prev.net_weight,
        weigh_bridge_ticket_number:
          ticketData?.weigh_bridge_ticket_number ||
          continue_processing_data?.weigh_bridge_ticket_number ||
          weighBridgeFormData?.weigh_bridge_ticket_number ||
          prev.weigh_bridge_ticket_number,
        weigh_bridge_ticket_reference_no:
          ticketData?.weigh_bridge_ticket_reference_no ||
          weighBridgeFormData?.weigh_bridge_ticket_reference_no ||
          prev.weigh_bridge_ticket_reference_no,
        truck_licence_plate:
          ticketData?.truck_licence_plate ||
          ticketData?.vehicle_number ||
          weighBridgeFormData?.truck_licence_plate ||
          weighBridgeFormData?.truck_license_plate ||
          prev.truck_licence_plate,
        transporter_name:
          ticketData?.transporter_name ||
          weighBridgeFormData?.transporter_name ||
          prev.transporter_name,
        truck_driver_name:
          ticketData?.truck_driver_name ||
          weighBridgeFormData?.truck_driver_name ||
          prev.truck_driver_name,
        trailer_one:
          ticketData?.trailer_one ||
          weighBridgeFormData?.trailer_one ||
          prev.trailer_one,
        trailer_two:
          ticketData?.trailer_two ||
          weighBridgeFormData?.trailer_two ||
          prev.trailer_two,
        comments:
          ticketData?.comments ||
          weighBridgeFormData?.comments ||
          prev.comments,
      }));
    } else {
      // Create mode: use current local state shape
      const dnId = continue_processing_data?.delivery_note_id;

      setRefData({
        ...continue_processing_data,
        first_weight:
          tareFromDb ??
          continue_processing_data?.first_weight ??
          weighBridgeFormData?.first_weight ??
          "",
        delivery_note_id: dnId ?? continue_processing_data?.delivery_note?.delivery_note_id,
      });

      setStepData((prev) => ({
        ...prev,
        delivery_note_id:
          dnId ??
          continue_processing_data?.delivery_note?.delivery_note_id ??
          prev.delivery_note_id,
        first_weight:
          tareFromDb ??
          continue_processing_data?.first_weight ??
          weighBridgeFormData?.first_weight ??
          prev.first_weight,
        second_weight:
          grossFromDb ??
          continue_processing_data?.second_weight ??
          weighBridgeFormData?.second_weight ??
          prev.second_weight,
        net_weight:
          netFromDb ??
          continue_processing_data?.net_weight ??
          weighBridgeFormData?.net_weight ??
          prev.net_weight,
        weigh_bridge_ticket_number:
          ticketData?.weigh_bridge_ticket_number ||
          weighBridgeFormData?.weigh_bridge_ticket_number ||
          prev.weigh_bridge_ticket_number,
        weigh_bridge_ticket_reference_no:
          ticketData?.weigh_bridge_ticket_reference_no ||
          weighBridgeFormData?.weigh_bridge_ticket_reference_no ||
          prev.weigh_bridge_ticket_reference_no,
        truck_licence_plate:
          ticketData?.truck_licence_plate ||
          ticketData?.vehicle_number ||
          weighBridgeFormData?.truck_licence_plate ||
          weighBridgeFormData?.truck_license_plate ||
          prev.truck_licence_plate,
        transporter_name:
          ticketData?.transporter_name ||
          weighBridgeFormData?.transporter_name ||
          prev.transporter_name,
        truck_driver_name:
          ticketData?.truck_driver_name ||
          weighBridgeFormData?.truck_driver_name ||
          prev.truck_driver_name,
        trailer_one:
          ticketData?.trailer_one ||
          weighBridgeFormData?.trailer_one ||
          prev.trailer_one,
        trailer_two:
          ticketData?.trailer_two ||
          weighBridgeFormData?.trailer_two ||
          prev.trailer_two,
        comments:
          ticketData?.comments ||
          weighBridgeFormData?.comments ||
          prev.comments,
      }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    delivery_note_ref_number,
    continue_processing_data,
    weighBridgeFormData,
    tareFromDb,
    grossFromDb,
    netFromDb,
    ticketData,
  ]);

  // ------------------------------------------------------
  // Handlers
  // ------------------------------------------------------
  const handleChange = (e) => {
    // If DB already has the weights, do not allow edits.
    if (weightsLocked) return;

    setStepData({ ...stepData, [e.target.name]: e.target.value });
  };

  const handleOnblur = (e) => {
    // If DB already has the weights, do not recalc.
    if (weightsLocked) return;

    if (e.target.name === "second_weight") {
      const tare = parseFloat(refData?.first_weight || 0);
      const gross = parseFloat(e.target.value || 0);

      if (!e.target.value) {
        setStepData((prev) => ({ ...prev, second_weight: "", net_weight: "" }));
        return;
      }

      // Guard: gross must be >= tare
      if (!isWeighbridgeWeightSequenceValid(tare, gross)) {
        setStepData((prev) => ({ ...prev, second_weight: "", net_weight: "" }));
        return;
      }

      setStepData((prev) => ({
        ...prev,
        second_weight: gross.toFixed(2),
        net_weight: resolveWeighbridgeNetWeight(prev.net_weight, tare, gross),
      }));
      return;
    }

    // generic number formatting
    if (e.target.value) {
      setStepData((prev) => ({
        ...prev,
        [e.target.name]: parseFloat(e.target.value).toFixed(2),
      }));
    }
  };

  const handleSubmit = () => {
    // NOTE:
    // Even if weights are locked (DB already has them), submitting should still be safe
    // because we send the existing values (idempotent backend is ideal).
    dispatch(
      employee_dashboard_actions_helper(
        loading_complete_processing_delivery_note,
        success_complete_processing_delivery_note,
        fail_complete_processing_delivery_note,
        COMPLETE_PROCESSING_DELIVERY_NOTE_ERR_MSG,
        COMPLETE_PROCESSING_DELIVERY_NOTE_SUCCESS_MSG,
        app_consts.POST,
        COMPLETE_PROCESSING_DELIVERY_NOTE_API,
        stepData,
        COMPLETE_PROCESSING_DELIVERY_NOTE_ACCESS_DENIED_MSG
      )
    );
  };

  const complete_processing_delivery_note = useSelector(
    (state) => state.complete_processing_delivery_note.complete_processing_delivery_note
  );

  useEffect(() => {
    if (complete_processing_delivery_note.status !== app_consts.IDLE) {
      if (complete_processing_delivery_note.status === app_consts.LOADING) {
        setErrorVal("");
        setLoading(true);
      } else {
        setLoading(false);
        if (complete_processing_delivery_note.status === app_consts.FAIL) {
          setErrorVal(complete_processing_delivery_note.message);
          messageToast(complete_processing_delivery_note.message, app_consts.FAIL);
          setOpenPreview(false);
        } else if (complete_processing_delivery_note.status === app_consts.SUCCESS) {
          setContinueProcessingData(complete_processing_delivery_note.data);
          messageToast(COMPLETE_PROCESSING_DELIVERY_NOTE_SUCCESS_MSG, app_consts.SUCCESS);
          nextStep();
        }
        dispatch(reset_complete_processing_delivery_note());
      }
    }
  }, [complete_processing_delivery_note, dispatch, nextStep, setContinueProcessingData]);

  const handlePreview = () => {
    setErrorVal("");
    setOpenPreview(true);
  };

  const canFinalize = useMemo(() => {
    // If DB has gross already -> allow finalize button (no need to type anything)
    if (weightsLocked) return true;

    // Otherwise, require gross
    return !!stepData?.second_weight;
  }, [weightsLocked, stepData?.second_weight]);

  return (
    <>
      <Card className="card-inner">
        <CardTitle tag="h5">Finilizing Delivery Note Details</CardTitle>

        {/* Banner */}
        {weightsLocked ? (
          <div className="alert alert-info mb-3">
            Weighbridge weights are already captured in the warehouse system. Fields are auto-filled and locked.
          </div>
        ) : ticketLocked ? (
          <div className="alert alert-info mb-3">
            This Delivery Note is linked to a Weighbridge ticket. Some fields may auto-fill.
          </div>
        ) : null}

        <Col lg="12">
          <div className="form-group">
            {weighBridgeFormData.consigner_type_id === app_consts.UAG_CONSIGNER_TYPE ? (
              <>
                <div className="form-label-group">
                  <label className="form-label text-soft">User Account ID :</label>
                </div>
                <div className="form-control-wrap">
                  <label className="form-label text-primary">UAG</label>
                </div>
              </>
            ) : (
              <>
                <div className="form-label-group">
                  <label className="form-label text-soft">User Account ID :</label>
                </div>
                <div className="form-control-wrap">
                  <label className="form-label text-primary">
                    {refData?.unique_identification_number || "---"}
                  </label>
                </div>
              </>
            )}
          </div>
        </Col>

        <hr />

        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Row className="g-4">
            <div className="form-label-group">
              <label className="form-label text-soft fs-22px">Weigh Bridge Details</label>
            </div>

            {/* TARE (always locked) */}
            <TextInput
              parentClassName="mt-1"
              colSize={6}
              label="Tare Weight (MT)"
              type="number"
              id_val="first_weight"
              value={refData?.first_weight || ""}
              name="first_weight"
              disabled
            />

            {/* GROSS (locked if already known in DB) */}
            <TextInput
              parentClassName="mt-1"
              colSize={6}
              label="Gross Weight (MT)"
              type="number"
              id_val="second_weight"
              value={stepData.second_weight || ""}
              handleChange={handleChange}
              name="second_weight"
              onBlur={handleOnblur}
              disabled={weightsLocked}
            />

            {/* NET (display only; prefer DB if present) */}
            <TextInput
              parentClassName="mt-1"
              colSize={6}
              label="Net Weight (MT)"
              type="number"
              id_val="net_weight"
              disabled
              value={stepData.net_weight || ""}
              name="net_weight"
            />

            <hr />

            <Col xl="12">
              <div className="form-group">
                <div className="form-control-wrap-flex-wrap justify-end items">
                  <Button
                    onClick={handlePreview}
                    disabled={loading ? loading : !canFinalize}
                    color="primary"
                    size="md"
                  >
                    {loading ? <Spinner size="sm" color="light" /> : "Preview & Finalize"}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {errorVal && (
            <div className="mt-4">
              <Alert color="danger" className="alert-icon">
                <Icon name="alert-circle" />
                {errorVal}
              </Alert>
            </div>
          )}
        </form>
      </Card>

      <ModalPreview
        openPreview={openPreview}
        setOpenPreview={setOpenPreview}
        modalTitle={`Finalize Delivery Note Details Preview`}
        modalDescription={`Please Confirm Delivery Note Details and Select 'Finalize and Save' to Fialize Details and Save Delivery Note.`}
        moduleButtonText="Finalize and Save"
        prevType={app_consts.FINAL_DELIVERY_NOTE_DETAILS}
        prevData={stepData}
        buttonFunction={handleSubmit}
      />
    </>
  );
};

export default FinalDetailsStep;
